import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { getWidgetStyle, KPIData, WidgetProps } from "@dto/Dashboard";
import { AggregationType } from "@dto/fields";
import { PathsResponse } from "@dto/records";
import { FIELD_SEPARATOR, SOURCE_SEPARATOR } from "@utils/constants";
import { getPathByFields } from "@utils/data";
import { toastError } from "@utils/toasts";
import { TW_PieChartLabel, TW_PieChartValue } from "@utils/twClasses";
import { valueToString } from "@utils/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";

export function KPICard(props: WidgetProps) {
    const widget = props.widget.data as KPIData;

    const objectInfos = useModelStore(state => state.objectInfos);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const [value, setValue] = useState<number | null>(null);
    const { t } = useTranslation();
    const pKey = widget.valuePath;
    const aggregation = widget.valueAggregation;
    const filter = widget.filter;


    useEffect(() => {
        if (!workspace) {
            return;
        }

        if (!pKey || !aggregation) {
            return;
        }

        let srcParts = pKey?.split(SOURCE_SEPARATOR);
        if (srcParts?.length !== 2) {
            return;
        }

        const keyParts = srcParts[1].split(FIELD_SEPARATOR);
        if (keyParts?.length !== 2) {
            return;
        }
    
        const objectKey = keyParts[0];
        const fieldKey = keyParts[1];
        
        const field = fieldByKeys?.get(fieldKey);
        if (!field) {
            return;
        }

        getPathByFields(workspace._key, objectKey, [field], relationByKeys, fieldByKeys, null, filter, null, null, null, null, aggregationMap).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    const pathsResponse = response as PathsResponse;
                    if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                        if (pathsResponse.lines[0].items.length > 0) {
                            setValue(pathsResponse.lines[0].items[0].value);
                        } else {
                            setValue(null);
                        }
                    } else {
                        setValue(null);
                    }
                }
            }
        })
    }, [objectInfos, aggregation, fieldByKeys, widget, filter]);


    if (!pKey || !aggregation) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    if (!workspace || !pKey) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    let srcParts = pKey?.split(SOURCE_SEPARATOR);
    if (srcParts?.length !== 2) {
        return;
    }

    const keyParts = srcParts[1].split(FIELD_SEPARATOR);
    if (keyParts?.length !== 2) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    const fieldKey = keyParts[1];
    const field = fieldByKeys?.get(fieldKey);
    if (!field) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    const aggregationMap = new Map<string, AggregationType>();
    if (pKey) {
        aggregationMap.set(pKey, aggregation);
    }

    const style = getWidgetStyle(props.widget);
    const format = field.format;
    return ( 
        <div className="flex flex-col gap-2 w-72 border-1 border-gray-200 rounded-md p-4 m-0" style={style}>
            <p className={TW_PieChartLabel}>{props.widget.title ?? field.name}</p>
            <p className={TW_PieChartValue}>{valueToString(value, field, aggregation, format, t, "-", fieldByKeys)}</p>
        </div>
    );
}